import { Binary as BSONBinary, ObjectId as BSONObjectId, EJSON } from 'bson';
import { z } from 'zod';
export const ejsonStringify = EJSON.stringify;
export const ejsonParse = EJSON.parse;

const ISO_DATETIME_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;

export const ISODateStringSchema = z
  .union([z.string(), z.date(), z.number()])
  .transform((d) => {
    if (d instanceof Date) return d.toISOString();
    if (typeof d === 'number') return new Date(d).toISOString();
    return d;
  })
  .refine((d) => ISO_DATETIME_REGEX.test(d), {
    message: 'Invalid ISO Date String.',
  });

export const ObjectIdHexStringSchema = z
  .string()
  .length(24)
  .refine(BSONObjectId.isValid, {
    message: 'Invalid object id. Has to be hex string with 24 chars.',
  })
  .describe('12 byte Object ID as Hex String.');

export const ObjectIdSchema = z
  .union([
    ObjectIdHexStringSchema,
    z.instanceof(Object),
    z.object({ _bsontype: z.literal('ObjectID'), id: z.string() }),
    z.object({ buffer: z.instanceof(Uint8Array) }),
  ])
  .refine(
    (oId) => {
      if (oId instanceof BSONObjectId) return true;
      if (typeof oId === 'object' && '_bsontype' in oId && oId._bsontype === 'ObjectID')
        return BSONObjectId.isValid(oId.id);
      if (typeof oId === 'object' && 'buffer' in oId && oId.buffer instanceof Uint8Array)
        return BSONObjectId.isValid(oId.buffer);

      return BSONObjectId.isValid(oId.toString());
    },
    {
      message: 'Invalid object id.',
    },
  )
  .transform((oId) => {
    if (oId instanceof BSONObjectId) return oId;
    if (typeof oId === 'object' && '_bsontype' in oId && oId._bsontype === 'ObjectID')
      return new BSONObjectId(oId.id);
    if (typeof oId === 'object' && 'buffer' in oId && oId.buffer instanceof Uint8Array)
      return new BSONObjectId(oId.buffer);

    return new BSONObjectId(oId.toString());
  })
  .describe('12 byte Object ID.');
export type ObjectId = z.infer<typeof ObjectIdSchema>;
export const ObjectId = BSONObjectId;
const Binary = BSONBinary;

export const DateSchema = z
  .union([z.date(), z.string(), z.number()])
  .transform((d) => {
    if (d instanceof Date) return d;
    return new Date(d);
  })
  .describe('Date object')
  .refine((d) => {
    if (d instanceof Date) return !Number.isNaN(d.getTime());
    return !Number.isNaN(new Date(d).getTime());
  });

export const BufferSchema = z
  .union([
    z.string(),
    z.instanceof(Buffer),
    z.instanceof(Binary),
    z.array(z.number()),
    z.object({ buffer: z.instanceof(Uint8Array) }),
    // TODO: remove if buffer comes as string
    z.object({ type: z.string(), data: z.array(z.number()) }),
  ])
  .transform((b) => {
    if (b instanceof Buffer) return b;
    if (b instanceof Binary) return Buffer.from(b.buffer);
    if (Array.isArray(b)) return Buffer.from(b);
    if (typeof b === 'string') return Buffer.from(b, 'base64');
    if (typeof b === 'object' && b !== null && 'buffer' in b && b.buffer instanceof Uint8Array)
      return Buffer.from(b.buffer);
    if (typeof b === 'object' && b !== null && 'data' in b) return Buffer.from(b.data);
    throw new Error('Unsupported buffer input.');
  });

export const PositiveIntegerSchema = z
  .union([z.string(), z.number()])
  .transform((n, ctx) => {
    try {
      if (typeof n === 'string') {
        if (n.trim() === '') {
          return undefined; // This will cause parsing to fail and use default
        }

        const parsed = Number.parseFloat(n);
        if (Number.isNaN(parsed))
          throw new Error(`Invalid positive integer string. ${n} is not a number.`);
        if (!Number.isInteger(parsed) || parsed <= 0)
          throw new Error(`Invalid positive integer string. ${n} is not a positive integer.`);
        return parsed;
      }
      if (typeof n === 'number') {
        if (!Number.isInteger(n) || n <= 0)
          throw new Error(`Invalid positive integer number. ${n} is not a positive integer.`);
        return n;
      }
      throw new Error('Invalid positive integer type. Expected number or string.');
    } catch (error) {
      if (error instanceof Error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: error.message,
        });
        return z.NEVER;
      }
      throw error;
    }
  })
  .describe('Positive integer number or string.');

/**
 * A boolean from a URL query parameter: `?flag=true`, `?flag=false`, or a bare `?flag`
 * (which express parses as an empty string) meaning true. Real booleans pass through, so
 * the same schema also works for JSON bodies.
 *
 * Anything else — `?flag=1`, `?flag=yes`, a typo — is a validation error rather than a
 * silently `false` flag, which matters for flags that gate writes (e.g. `dryRun`).
 */
export const BooleanQuerySchema = z
  .union([z.boolean(), z.enum(['true', 'false', ''])])
  .transform((b) => (typeof b === 'boolean' ? b : b !== 'false'))
  .describe('Boolean, or "true" / "false" / "" as passed in a query parameter.');

/**
 * A url the tracking app can actually open. Its WebView is bound by Android's cleartext
 * ban and iOS ATS, so an `http://` url yields a button that does nothing on the phone.
 *
 * `''` is the "not configured" state of these fields and stays valid.
 */
export const OptionalHttpsUrlSchema = z
  .union([z.literal(''), z.url({ protocol: /^https$/ })])
  .describe('Empty string, or an https:// url.');

type InferKey<T extends z.ZodType> = z.infer<T>;
type InferValue<T extends z.ZodType> = z.infer<T>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value.constructor === Object || Object.getPrototypeOf(value) === null)
  );
}

export function mapFromObjectOrMapStrict<KSchema extends z.ZodType, VSchema extends z.ZodType>(
  keySchema: KSchema,
  valueSchema: VSchema,
): z.ZodType<Map<InferKey<KSchema>, InferValue<VSchema>>> {
  const mapSchema = z.map(keySchema, valueSchema);

  return z.preprocess((input) => {
    if (input instanceof Map) return input;
    if (isPlainObject(input)) return new Map(Object.entries(input));
    return input;
  }, mapSchema) as z.ZodType<Map<InferKey<KSchema>, InferValue<VSchema>>>;
}
